import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import { Form, Group, Input, Heading, LoadingIcon } from '@peracto/peracto-ui'
import { useConfig } from '@peracto/peracto-config'

import { CodeFormatter } from '../../../components/CodeFormatter'
import { CopyButton } from '../../../components/CopyButton'
import { authorisedGet } from '../util'

const schema = yup.object().shape({
    skus: yup.string().required('At least one SKU is required'),
})

const getStockRows = (result) => {
    const payload = result?.response?.data?.payload || []

    return result.request.map((sku) => {
        const stock = payload.find(
            (item) => String(item?.stkcode).toLowerCase() === sku.toLowerCase(),
        )

        return {
            sku,
            returned: !!stock,
            pacificaStock: Number(stock?.stkquantinstock ?? 0),
            supplierStock: Number(stock?.supplier_quantinstock ?? 0),
        }
    })
}

const CodeGroup = ({ id, name, title, json, text }) => (
    <Group key={id} id={id} name={name}>
        <div className="flex items-center justify-between mt-2 mb-3">
            <h4 className="text-[18px] font-semibold mr-2 mb-0">{title}</h4>{' '}
            <CopyButton content={json !== undefined ? JSON.stringify(json, null, 2) : text} />
        </div>
        <div className="border rounded-[4px] p-2">
            {json !== undefined ? (
                <CodeFormatter code={json} />
            ) : (
                <pre className="mb-0 whitespace-pre-wrap break-words">{text}</pre>
            )}
        </div>
    </Group>
)

const ResponseGroup = ({ result }) => {
    const title = `Pacifica Stock Response${
        result.responseStatusCode ? ` (HTTP ${result.responseStatusCode})` : ''
    }`

    if (result.response) {
        return <CodeGroup id="response" name="Raw Response" title={title} json={result.response} />
    }

    if (result.rawResponse) {
        return <CodeGroup id="response" name="Raw Response" title={title} text={result.rawResponse} />
    }

    return (
        <Group key="response" id="response" name="Raw Response">
            <p className="mb-0">
                {result.responseStatusCode
                    ? `Pacifica responded with HTTP ${result.responseStatusCode} and an empty body.`
                    : 'No stock request was sent to Pacifica, see the error above.'}
            </p>
        </Group>
    )
}

export const PacificaStockCheckForm = () => {
    const config = useConfig()
    const API_URL = config.get('api')

    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)

    const onSubmitForm = async (formData, actions) => {
        setLoading(true)
        setResult(null)

        try {
            const skus = (formData?.skus || '')
                .split(/[\s,]+/)
                .filter(Boolean)
                .join(',')

            const { data } = await authorisedGet(
                API_URL,
                `/test-harness/pacifica-stock?skus=${encodeURIComponent(skus)}`,
            )

            setResult(data)

            if (!data.success) {
                toast.error(data.error || 'The Pacifica stock request failed')
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.['hydra:description'] || e?.response?.data?.detail || e?.message,
            )
        } finally {
            setLoading(false)
            actions.setSubmitting(false)
        }
    }

    return (
        <>
            <Helmet>
                <title>Pacifica Stock Check | Test Harnesses | Peracto</title>
            </Helmet>
            <div className="form-container">
                <Heading name="Pacifica Stock Check" />
                <Form
                    autoComplete="on"
                    values={{
                        skus: '',
                    }}
                    schema={schema}
                    showActionPanel={false}
                    onSubmit={onSubmitForm}
                >
                    <Group key="request" id="request" name="Request">
                        <p>
                            Queries Pacifica directly, bypassing the website's 5 minute stock cache,
                            and shows the payload sent and the raw response.
                        </p>
                        <Input
                            name="skus"
                            label="SKUs"
                            testId="skus"
                            required
                            help="One or more SKUs, separated by commas or spaces (maximum 50)"
                        />

                        <button className="btn btn-primary" type="submit" data-testid="submit">
                            Check Stock
                        </button>
                    </Group>

                    {loading && (
                        <div className="flex items-center justify-center py-5">
                            <LoadingIcon />
                        </div>
                    )}

                    {result && !loading && (
                        <>
                            <Group key="summary" id="summary" name="Stock Summary">
                                <p>
                                    {result.success ? 'Request succeeded' : `Request failed: ${result.error}`}
                                    {` in ${result.durationMs}ms at ${new Date(
                                        result.checkedAt,
                                    ).toLocaleString('en-GB')}`}
                                </p>

                                {result.success && (
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>SKU</th>
                                                <th>Returned by Pacifica</th>
                                                <th>Pacifica Stock</th>
                                                <th>Supplier Stock</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getStockRows(result).map((row) => (
                                                <tr key={row.sku}>
                                                    <td>{row.sku}</td>
                                                    <td className={row.returned ? '' : 'text-danger'}>
                                                        {row.returned ? 'Yes' : 'No'}
                                                    </td>
                                                    <td>{row.pacificaStock}</td>
                                                    <td>{row.supplierStock}</td>
                                                    <td>{row.pacificaStock + row.supplierStock}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </Group>

                            <CodeGroup
                                id="request-payload"
                                name="Request Payload"
                                title="Payload Sent to Pacifica"
                                json={result.request}
                            />

                            <ResponseGroup result={result} />
                        </>
                    )}
                </Form>
            </div>
        </>
    )
}

PacificaStockCheckForm.displayName = 'PacificaStockCheckForm'
